/**
 * ModulePaywall — shown when a user navigates to a route whose module is
 * disabled for their org. Owners get an "Enable" CTA that toggles the module
 * via the same backend endpoint as Settings. Non-owners see a request-access
 * hint instead.
 *
 * In a later phase this becomes a real Stripe billing CTA.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useModules } from '../context/ModulesContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const TIER_LABEL = {
  free: 'Free',
  starter: 'Starter plan',
  pro: 'Pro plan',
  franchise: 'Franchise plan',
};

export default function ModulePaywall({ moduleKey, routePath }) {
  const { catalogue, toggleModule } = useModules();
  const { user } = useAuth();
  const navigate = useNavigate();

  const meta = catalogue.find((m) => m.key === moduleKey);
  const role = (user?.role || '').toLowerCase();
  const canSelfEnable = role === 'owner' || role === 'manager' || role === 'admin';

  const handleEnable = async () => {
    try {
      await toggleModule(moduleKey, true);
      toast.success(`${meta?.display_name || moduleKey} enabled.`);
      // Reload so the page reads fresh module state and renders normally
      navigate(routePath || 0, { replace: true });
      setTimeout(() => window.location.reload(), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to enable module');
    }
  };

  if (!meta) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8 text-center">
          <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Module not available</h2>
          <p className="text-sm text-muted-foreground">
            This area is not available on your current plan.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="module-paywall">
      <Card className="max-w-lg w-full p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {TIER_LABEL[meta.tier] || meta.tier} feature
            </div>
            <h2 className="text-2xl font-semibold">{meta.display_name}</h2>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-6">{meta.description}</p>

        {meta.coming_soon ? (
          <div className="bg-muted/40 rounded-md p-4 text-sm">
            <div className="flex items-center gap-2 mb-1 font-medium">
              <Sparkles className="w-4 h-4" /> Coming soon
            </div>
            We&apos;re actively building this — keep an eye on the changelog.
          </div>
        ) : canSelfEnable ? (
          <div className="space-y-3">
            <Button
              onClick={handleEnable}
              data-testid={`enable-module-${moduleKey}-btn`}
              className="w-full"
              size="lg"
            >
              Enable {meta.display_name}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Free during preview. Will be part of the {TIER_LABEL[meta.tier]?.toLowerCase() || meta.tier} when billing launches — existing data is preserved when toggling.
            </p>
          </div>
        ) : (
          <div className="bg-muted/40 rounded-md p-4 text-sm">
            <strong className="block mb-1">Ask your owner to enable this module.</strong>
            Only owners and admins can change which modules are active for the
            organisation. Send them a link to <code className="text-xs bg-background px-1.5 py-0.5 rounded">Settings → Modules</code>.
          </div>
        )}
      </Card>
    </div>
  );
}
